import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'

import useTranslate from '../../../../../lib/hooks/use-translate'
import {
  AsthmaSeverityEnum,
  asthmaSeverityMapping
} from '../../../../../lib/types/enums/affected-staff-questionnaire/asthma-severity.enum'
import { AffectedStaffQuestionnaireInput } from '../../../../../lib/types/models/affected-staff-questionnaire/affected-staff-questionnaire-input'
import { FormControl, FormField } from '../../../../elements/forms/form'
import ControlledInput from '../../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../../elements/inputs/controlled-radio-input'

const StaffPersonalSpaceQuestionnaireHealthSection = () => {
  const { t } = useTranslate()
  const form = useFormContext<AffectedStaffQuestionnaireInput>()
  return (
    <InnerBox
      title={t('affected.staff.questionnaire.health.information.title')}
      className="p-4 rounded-lg "
    >
      <div className="flex flex-col gap-3">
        <div className="flex gap-5 items-center mobile:flex-col mobile:items-start mobile:gap-2">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            {t('affected.staff.questionnaire.health.information.are.you.in.good.health')}
          </Typography>
          <div className="flex items-center gap-4">
            <FormField
              control={form.control}
              name={'isInGoodHealth'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.yes')}
                    checked={field.value === true}
                    onCheck={() => form.setValue('isInGoodHealth', true, { shouldValidate: true })}
                  />
                </FormControl>
              )}
            />
            <FormField
              control={form.control}
              name={'isInGoodHealth'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.no')}
                    checked={field.value === false}
                    onCheck={() =>
                      form.setValue('isInGoodHealth', false, { shouldValidate: true })
                    }
                  />
                </FormControl>
              )}
            />
          </div>
        </div>
        <div className="flex gap-5 items-center mobile:flex-col mobile:items-start mobile:gap-2">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            {t('affected.staff.questionnaire.health.information.do.you.have.asthma')}
          </Typography>
          <div className="flex items-center gap-4">
            <FormField
              control={form.control}
              name={'hasAsthma'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.yes')}
                    checked={field.value === true}
                    onCheck={() => form.setValue('hasAsthma', true, { shouldValidate: true })}
                  />
                </FormControl>
              )}
            />
            <FormField
              control={form.control}
              name={'hasAsthma'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.no')}
                    checked={field.value === false}
                    onCheck={() => {
                      form.setValue('hasAsthma', false, { shouldValidate: true })
                      form.setValue('asthmaSeverity', undefined, { shouldValidate: true })
                    }}
                  />
                </FormControl>
              )}
            />
          </div>
        </div>
        {form.watch('hasAsthma') && (
          <InnerBox className="bg-white-hover p-2 w-fit">
            <div className="flex gap-3 items-center  ">
              {Object.values(AsthmaSeverityEnum).map((severity) => (
                <FormField
                  key={severity}
                  control={form.control}
                  name={`asthmaSeverity`}
                  render={({ field }) => (
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={t(asthmaSeverityMapping[severity])}
                        checked={field.value === severity}
                        onCheck={() =>
                          form.setValue(`asthmaSeverity`, severity, { shouldValidate: true })
                        }
                      />
                    </FormControl>
                  )}
                />
              ))}
            </div>
          </InnerBox>
        )}

        <div className="flex gap-5 items-center mobile:flex-col mobile:items-start mobile:gap-2">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            {t('affected.staff.questionnaire.health.information.do.you.have.allergy')}
          </Typography>
          <div className="flex items-center gap-4">
            <FormField
              control={form.control}
              name={'hasAllergies'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.yes')}
                    checked={field.value === true}
                    onCheck={() => form.setValue('hasAllergies', true, { shouldValidate: true })}
                  />
                </FormControl>
              )}
            />
            <FormField
              control={form.control}
              name={'hasAllergies'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.no')}
                    checked={field.value === false}
                    onCheck={() => {
                      form.setValue('hasAllergies', false, { shouldValidate: true })
                      form.setValue('allergyType', undefined, { shouldValidate: true })
                    }}
                  />
                </FormControl>
              )}
            />
          </div>
        </div>
        {form.watch('hasAllergies') && (
          <FormField
            control={form.control}
            name={'allergyType'}
            render={({ field }) => (
              <FormControl>
                <ControlledInput
                  {...field}
                  type="text"
                  label={t('affected.staff.questionnaire.which.one')}
                />
              </FormControl>
            )}
          />
        )}

        <div className="flex gap-5 items-center mobile:flex-col mobile:items-start mobile:gap-2">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            {t('affected.staff.questionnaire.health.information.do.you.smoke')}
          </Typography>
          <div className="flex items-center gap-4">
            <FormField
              control={form.control}
              name={'isSmoker'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.yes')}
                    checked={field.value === true}
                    onCheck={() => form.setValue('isSmoker', true, { shouldValidate: true })}
                  />
                </FormControl>
              )}
            />
            <FormField
              control={form.control}
              name={'isSmoker'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={t('staff.personal.space.no')}
                    checked={field.value === false}
                    onCheck={() => {
                      form.setValue('isSmoker', false, { shouldValidate: true })
                      form.setValue('agreesToFamilyLifeStyle', undefined, { shouldValidate: true })
                    }}
                  />
                </FormControl>
              )}
            />
          </div>
        </div>
        {form.watch('isSmoker') && (
          <InnerBox className="py-2 px-3 bg-white-hover">
            <div className="flex items-center gap-2.5">
              <Checkbox
                checked={form.watch('agreesToFamilyLifeStyle') === true}
                onChange={(checked) =>
                  form.setValue('agreesToFamilyLifeStyle', checked, { shouldValidate: true })
                }
              />
              <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                {t('affected.staff.questionnaire.health.information.agree.to.family.lifestyle')}
              </Typography>
            </div>
          </InnerBox>
        )}
        <div className="flex flex-col gap-1">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            {t('affected.staff.questionnaire.health.information.other.information')}
          </Typography>
          <FormField
            control={form.control}
            name={'otherInformationInEnglish'}
            render={({ field }) => (
              <FormControl>
                <ControlledInput
                  {...field}
                  type="textarea"
                  placeholder={t(
                    'affected.staff.questionnaire.health.information.other.information.placeholder'
                  )}
                />
              </FormControl>
            )}
          />
        </div>
        <Box className="bg-primary-light rounded p-3">
          <Typography variant="label-semibold" color={tailwindColors.primary.DEFAULT}>
            {t('affected.staff.questionnaire.health.information.health.note')}
          </Typography>
        </Box>
      </div>
    </InnerBox>
  )
}

export default StaffPersonalSpaceQuestionnaireHealthSection
